/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { DOCS_LINKS } from 'constants';

import { connect } from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'carbon-addons-iot-react';
import { injectIntl, defineMessages } from 'react-intl';
import { Add16 } from '@carbon/icons-react';
import { useField } from 'formik';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import cloneDeep from 'lodash/cloneDeep';
import classnames from 'classnames';

import { getCurrentUser } from '../../reducers/userReducer';
import { UserDataPropTypes } from '../../constants/CommonPropTypes';

import * as utils from './metadataEditorUtils';

import {
  TextInputFormik,
  ComboBoxFormik,
  ToggleSmallFormik,
  NumberInputFormik,
  DropdownFormik,
  JsonEditorFormik,
  JsonSchemas,
  TableEmptyState,
  SimpleCommonTable,
} from 'Common';

const messages = defineMessages({
  key: 'Name',
  value: 'Value',
  type: 'Type',
  valuePlaceholder: 'Enter a value for the property',
  addProperty: 'Add dimension',
  removeProperty: 'Remove',
  valueToggleAriaLabel: 'Boolean property toggle',
  noDimensions: 'No dimensions are currently defined',

  // empty table messages
  noDataNoAccessMessage: 'No dimensions are defined.',
  noDataMessage: 'No dimensions are defined. Click Add dimension to get started.',
  noDataLinkText: 'Learn about dimensions',
});

const mapStateToProps = state => ({
  userData: getCurrentUser(state),
});

const mapDispatchToProps = () => ({});

const propTypes = {
  formikNamespace: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  inSourceView: PropTypes.bool,
  inAdminMode: PropTypes.bool,
  onChangeInputStatus: PropTypes.func,
  userData: UserDataPropTypes,
};

const defaultProps = {
  readOnly: true,
  inSourceView: false,
  inAdminMode: false,
  onChangeInputStatus: noop,
  userData: {},
};

/**
 * Forces associated type: string if key is one of the standard deviceInfo fields
 */
const KeyInput = ({ fieldId, disabled, items }) => {
  const keyFieldId = `${fieldId}.key`;
  const typeFieldId = `${fieldId}.type`;
  const valueFieldId = `${fieldId}.value`;

  const [typeField, , typeHelpers] = useField(typeFieldId);
  const [, , valueHelpers] = useField(valueFieldId);

  return (
    <ComboBoxFormik
      fieldId={keyFieldId}
      disabled={disabled}
      items={items}
      onInputChange={v => {
        if (typeField.value !== utils.DATA_TYPES.STRING && utils.isDeviceInfoKey(v)) {
          typeHelpers.setValue(utils.DATA_TYPES.STRING);
          valueHelpers.setValue(utils.getDefaultForType(utils.DATA_TYPES.STRING)); // and reset the value to the empty string
        }
      }}
    />
  );
};
KeyInput.propTypes = {
  fieldId: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired, // eslint-disable-line react/boolean-prop-naming
  // should only include deviceInfo keys that have not been used on other rows
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Adaptive input type
 */
const ValueInput = ({ formikNamespace, disabled, formatMessage }) => {
  const [typeField, ,] = useField(`${formikNamespace}.type`);

  if (typeField.value === utils.DATA_TYPES.STRING) {
    return (
      <TextInputFormik
        id={`${formikNamespace}.value`}
        disabled={disabled}
        label=""
        hideLabel
        placeholder={formatMessage(messages.valuePlaceholder)}
      />
    );
  }

  if (typeField.value === utils.DATA_TYPES.BOOLEAN) {
    return (
      <ToggleSmallFormik
        id={`${formikNamespace}.value`}
        disabled={disabled}
        aria-label={formatMessage(messages.valueToggleAriaLabel)}
        labelA="False"
        labelB="True"
      />
    );
  }

  if (typeField.value === utils.DATA_TYPES.NUMBER) {
    return <NumberInputFormik id={`${formikNamespace}.value`} disabled={disabled} hideLabel />;
  }

  return null;
};
ValueInput.propTypes = {
  formikNamespace: PropTypes.string.isRequired,
  disabled: PropTypes.bool, // eslint-disable-line react/boolean-prop-naming
  formatMessage: PropTypes.func.isRequired,
};
ValueInput.defaultProps = {
  disabled: false,
};

/**
 * Dropdown for property type that also replaces the associated value field
 * with a default of the correct type whenever it changes
 *
 * Force disabled if associated key is one of the standard deviceInfo fields
 */
const TypeInput = ({ formikNamespace, disabled }) => {
  const keyFieldId = `${formikNamespace}.key`;
  const typeFieldId = `${formikNamespace}.type`;
  const valueFieldId = `${formikNamespace}.value`;

  const [keyField] = useField(keyFieldId);
  const [, , valueHelpers] = useField(valueFieldId);

  return (
    <DropdownFormik
      id={typeFieldId}
      // OBJECT/ARRAY cannot be selected (rows of this type are hidden in table view)
      items={[utils.DATA_TYPES.STRING, utils.DATA_TYPES.NUMBER, utils.DATA_TYPES.BOOLEAN]}
      disabled={disabled || utils.isDeviceInfoKey(keyField.value)}
      // this ensures it is rendered as a proper disabled dropdown when not read-only but is device info key
      renderAsTextWhenDisabled={!utils.isDeviceInfoKey(keyField.value) || disabled}
      onChange={({ selectedItem }) => {
        valueHelpers.setValue(utils.getDefaultForType(selectedItem));
      }}
      hideLabel
    />
  );
};
TypeInput.propTypes = {
  formikNamespace: PropTypes.string.isRequired,
  disabled: PropTypes.bool, // eslint-disable-line react/boolean-prop-naming
};
TypeInput.defaultProps = {
  disabled: false,
};

const MetadataEditor = ({
  formikNamespace,
  readOnly,
  inSourceView,
  inAdminMode,
  intl: { formatMessage },
  onChangeInputStatus,
  userData,
}) => {
  const docURL = userData ? userData.docURL : null;
  const [field, , helpers] = useField(formikNamespace);

  const isLoading = isNil(field.value) || field.value === '';

  const normalizedFieldValue = isNil(field.value) || field.value === '' ? [] : field.value;

  const onAddProperty = () => {
    helpers.setValue([
      ...normalizedFieldValue,
      {
        key: '',
        value: utils.getDefaultForType(utils.DATA_TYPES.STRING),
        type: utils.DATA_TYPES.STRING,
      },
    ]);
  };

  const onRemoveProperty = rowId => {
    // rowId e.g.: [3]
    const parts = /\[(\d+)\]/.exec(rowId);
    const indexOfPropertyToRemove = parts[1];
    // splice modified array, so apply to a (deep) clone of the field value
    const clone = cloneDeep(normalizedFieldValue);
    clone.splice(indexOfPropertyToRemove, 1);
    helpers.setValue(clone);
  };

  const tableData = !isLoading
    ? utils.mapToTableData(
        normalizedFieldValue,
        readOnly,
        inAdminMode ? { labelText: formatMessage(messages.removeProperty) } : undefined
      )
    : [];

  const unusedDeviceInfoKeys = isLoading
    ? utils.DEVICE_INFO_KEYS
    : utils.DEVICE_INFO_KEYS.filter(
        infoKey => !normalizedFieldValue.some(({ key }) => key === infoKey)
      );

  return inSourceView ? (
    <JsonEditorFormik
      fieldId={formikNamespace}
      options={{
        readOnly,
      }}
      formikModelToJson={model => utils.formikToRedux(model)}
      jsonToFormikModel={json => utils.reduxToFormik(json)}
      onChangeParseErrorStatus={onChangeInputStatus}
      schema={JsonSchemas.WIOTP_DEVICE_INFO_METADATA}
      isLoading={isLoading}
    />
  ) : (
    <SimpleCommonTable
      id="metadata-editor"
      className={classnames('metadata-editor', { 'metadata-editor-loading': isLoading })}
      columns={[
        {
          id: 'key',
          name: formatMessage(messages.key),
          isSortable: false,
          // eslint-disable-next-line react/prop-types
          renderDataFunction: ({ rowId }) => (
            <div className="centered-cell-wrapper">
              <KeyInput
                fieldId={`${formikNamespace}${rowId}`}
                items={unusedDeviceInfoKeys}
                disabled={readOnly || !inAdminMode}
              />
            </div>
          ),
        },
        ...(inAdminMode
          ? [
              {
                id: 'type',
                name: formatMessage(messages.type),
                isSortable: false,
                // eslint-disable-next-line react/prop-types
                renderDataFunction: ({ rowId }) => (
                  <div className="centered-cell-wrapper">
                    <TypeInput formikNamespace={`${formikNamespace}${rowId}`} disabled={readOnly} />
                  </div>
                ),
              },
            ]
          : []),
        {
          id: 'value',
          name: formatMessage(messages.value),
          isSortable: false,
          // eslint-disable-next-line react/prop-types
          renderDataFunction: ({ rowId }) => (
            <div className="centered-cell-wrapper">
              <ValueInput
                formikNamespace={`${formikNamespace}${rowId}`}
                disabled={readOnly}
                formatMessage={formatMessage}
              />
            </div>
          ),
        },
      ]}
      data={tableData || []}
      view={{
        table: {
          loadingState: {
            isLoading,
            rowCount: 1,
          },
          emptyState: (
            <TableEmptyState
              message={formatMessage(
                inAdminMode ? messages.noDataMessage : messages.noDataNoAccessMessage
              )}
              linkText={formatMessage(messages.noDataLinkText)}
              linkHref={`${docURL}${DOCS_LINKS.ADD_DIMENSIONS}`}
            />
          ),
        },
        toolbar:
          inAdminMode && !readOnly
            ? {
                customToolbarContent: (
                  <Button
                    onClick={onAddProperty}
                    disabled={readOnly || isLoading}
                    kind="secondary"
                    renderIcon={Add16}>
                    {formatMessage(messages.addProperty)}
                  </Button>
                ),
              }
            : undefined,
      }}
      options={{
        hasPagination: false,
        hasFilter: false,
        hasRowActions: inAdminMode,
        hasSearch: false,
        hasColumnSelection: false,
        hasRowSelection: false,
        hasOnlyPageData: true,
        shouldExpandOnRowClick: false,
        hasRowExpansion: false,
        hasRowNesting: false,
      }}
      // suppress console warnings due to undefined table actions
      actions={{
        pagination: {
          onChangePage: noop,
        },
        table: {
          onApplyRowAction: (actionId, rowId) => {
            if (actionId === utils.ROW_ACTION_IDS.REMOVE_PROPERTY) {
              onRemoveProperty(rowId);
            }
          },
          onChangeOrdering: noop,
          onChangeSort: noop,
          onColumnResize: noop,
          onColumnSelectionConfig: noop,
          onEmptyStateAction: () => () => noop,
          onRowClicked: noop,
          onRowExpanded: () => noop,
          onRowSelected: noop,
          onSelectAll: noop,
        },
        toolbar: {
          onApplyBatchAction: noop,
          onApplyFilter: noop,
          onApplySearch: noop,
          onCancelBatchAction: noop,
          onClearAllFilters: noop,
          onToggleColumnSelection: noop,
          onToggleFilter: noop,
        },
      }}
    />
  );
};

MetadataEditor.propTypes = propTypes;
MetadataEditor.defaultProps = defaultProps;
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(MetadataEditor));
